import { useQuery } from "@tanstack/react-query"

import { useAuthStore } from "@/entities/auth/auth.model"

import { getAssistantAccess } from "./assistant.api"
import { assistantKeys } from "./assistant.queries"

/**
 * Whether to offer the tool to the signed-in user.
 *
 * The answer comes from the server rather than from a domain list held here. The list used to be
 * duplicated into this file, which meant shipping the internal domains to every browser that
 * loaded the bundle — no access leaked, since the server refuses regardless, but which domains
 * are internal is not something to publish. It also had to be kept in step by hand.
 *
 * `isPending` is returned rather than folded into a boolean so callers can hold the entry back
 * during the first load instead of flashing it in and out.
 */
export const useIsStaffAccount = () => {
  const { isAuthenticated } = useAuthStore()

  const { data, isPending, isError } = useQuery({
    queryKey: assistantKeys.access(),
    queryFn: getAssistantAccess,
    enabled: isAuthenticated,
    // Asked once per session rather than per navigation: the nav bar renders on every page, and
    // whether someone is staff does not change while they are looking at it.
    staleTime: 10 * 60 * 1000,
  })

  return {
    isStaff: data?.allowed === true,
    reviewEffortSupported: data?.allowed === true && data.reviewEffortSupported === true,
    // Reported apart from `isStaff` so an unreachable check is not rendered as a refusal. A 5xx
    // or a timeout would otherwise tell an employee the tool is staff-only, which is both wrong
    // and unactionable.
    //
    // Only when there is no answer at all. A failed *background* refetch leaves the last good
    // result in `data` while setting `isError`, and reporting unavailable then would replace a
    // working page for someone whose access is already known.
    isUnavailable: isError && data === undefined,
    // A disabled query never leaves `isPending`, so a signed-out visitor would otherwise sit in a
    // permanent loading state instead of simply not being staff.
    isPending: isAuthenticated && isPending,
  }
}
